/**
 * ReferralInviteBlock.jsx — Shared referral invite block (NOT a modal, inner content only).
 *
 * Used by both ReferralQuickShareModal (modal) and the Settings ReferralSection (page),
 * so both surfaces share ONE component (§10 template parent, §9 DRY, §74 search-before-create).
 *
 * Top to bottom:
 *   1. URL row — read-only referral link + copy icon button on the right (clipboard + "Copied!" 2s).
 *   2. Email invite block — multi-email chip input ("Add their email") + "Invite" button.
 *
 * §11/§18 — composes existing UIX primitives. §20 — zero color/font/radius literals (theme.js only).
 * §64 — English only. §65 — earnicoins (never EIC). §89 — no em dash. §12ter — responsive rem, breakpoint.
 * §12undecies — grammatical case.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { COLORS, FONTS, RADII, FONT_SIZE, FONT_WEIGHT, media } from './theme';
import { CtaButton } from './daComponents';
import { sendReferralInvite, getMyReferral } from '../../services/ReferralService';

/* ─── URL row ─────────────────────────────────────────────────────────────────── */

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${COLORS.bg};
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.button};
  padding: 0.375rem 0.5rem 0.375rem 0.875rem;
`;

const LinkInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.sm};
  color: ${COLORS.ink};
  cursor: text;
  min-width: 0;
`;

const CopyIconButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  background: transparent;
  border: 0.0625rem solid ${COLORS.lineStrong};
  border-radius: ${RADII.button};
  padding: 0.5rem 0.625rem;
  color: ${COLORS.ink};
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  &:hover { background: ${COLORS.subtle}; border-color: ${COLORS.accent}; }
  &.is-copied { border-color: ${COLORS.cta}; color: ${COLORS.cta}; }

  svg { width: 1.125rem; height: 1.125rem; display: block; }
  span {
    font-family: ${FONTS.head};
    font-weight: ${FONT_WEIGHT.bold};
    font-size: ${FONT_SIZE.xs};
  }
`;

/* ─── Email invite (multi-row) ────────────────────────────────────────────────── */

/* Bloc dédié à l'invitation : liseré magenta léger + léger fond teinté, espacé du
   bloc URL au-dessus. Regroupe le titre + la saisie email + les actions. */
const InviteBox = styled.div`
  margin-top: 1.75rem;
  padding: 1.125rem 1.25rem 1.25rem;
  border: 0.0625rem solid ${COLORS.accent45};
  border-radius: ${RADII.card};
  background: ${COLORS.accentSoft};
`;

/* div (pas <p>) : à l'intérieur d'une modal, ModalBody impose `p { color: encre }`
   qui écraserait la couleur magenta de ce label (§20). Un div y échappe. */
const Label = styled.div`
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: ${FONT_SIZE.sm};
  color: ${COLORS.accent};
  text-transform: none;
  letter-spacing: 0;
  margin: 0 0 0.75rem;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const EmailInput = styled.input`
  flex: 1;
  min-width: 0;
  background: ${COLORS.white};
  border: 0.0625rem solid ${({ $invalid }) => ($invalid ? COLORS.danger : COLORS.line)};
  border-radius: ${RADII.button};
  padding: 0.625rem 0.875rem;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.sm};
  color: ${COLORS.ink};
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: ${({ $invalid }) => ($invalid ? COLORS.danger : COLORS.accent)}; }
  &::placeholder { color: ${COLORS.muted}; }
`;

const RemoveRowBtn = styled.button`
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.button};
  color: ${COLORS.muted};
  font-size: ${FONT_SIZE.sm};
  line-height: 1;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;
  &:hover { color: ${COLORS.danger}; border-color: ${COLORS.danger}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const AddRowBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: transparent;
  border: none;
  padding: 0.25rem 0;
  margin-bottom: 0.5rem;
  color: ${COLORS.accent};
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.semibold};
  font-size: ${FONT_SIZE.xs};
  cursor: pointer;
  &:hover { text-decoration: underline; }
  &:disabled { color: ${COLORS.muted}; cursor: default; text-decoration: none; }
`;

const InviteFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.625rem;
  gap: 0.5rem;
  ${media.sm`flex-direction: column; align-items: stretch;`}
`;

/* div (pas <p>) : échappe au `p { color }` de ModalBody pour garder muted/danger. */
const InviteStatus = styled.div`
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  margin: 0;
  color: ${({ $error }) => ($error ? COLORS.danger : COLORS.muted)};
`;

/* Total cumulé d'invitations envoyées par l'utilisateur (compteur à droite). */
const TotalInvites = styled.div`
  flex-shrink: 0;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.muted};
  b {
    font-weight: ${FONT_WEIGHT.bold};
    color: ${COLORS.accent};
  }
`;

/* ─── Inline SVG copy icons (no icon lib in repo) ─────────────────────────────── */

const CopyGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);

const CheckGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Validation stricte du format email (anti-injection : rejette espaces, <>"'`, etc.).
// Le backend revalide + requêtes paramétrées + React Email échappe → défense en profondeur.
const STRICT_EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const MAX_EMAIL_LEN = 254; // RFC 5321
const MAX_ROWS = 50;       // aligné sur MAX_PER_REQUEST backend

function isValidEmail(raw) {
  const v = String(raw).trim().toLowerCase();
  return v.length > 0 && v.length <= MAX_EMAIL_LEN && STRICT_EMAIL_RE.test(v);
}

/* ─── Component ─────────────────────────────────────────────────────────────────
 * @param {object}   props
 * @param {string}   props.referralLink   - Referral URL to display + copy.
 * @param {function} [props.onInvited]    - Called with the sent emails array on success.
 */
export default function ReferralInviteBlock({ referralLink = '', onInvited }) {
  const [copied, setCopied] = useState(false);
  const idCounter = useRef(1);
  const [rows, setRows] = useState([{ id: 0, value: '' }]); // une ligne email au départ
  const [inviteStatus, setInviteStatus] = useState(null);   // null | 'sending' | 'sent' | 'error'
  const [inviteMessage, setInviteMessage] = useState('');
  const [totalInvites, setTotalInvites] = useState(0);      // total cumulé envoyé par l'user

  // Total d'invitations déjà envoyées (self-contained : marche en modal ET settings)
  useEffect(() => {
    let alive = true;
    getMyReferral()
      .then((me) => { if (alive && Number.isFinite(me?.stats?.invitesSent)) setTotalInvites(me.stats.invitesSent); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleCopy = useCallback(() => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [referralLink]);

  const setRowValue = useCallback((id, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, value } : r)));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => (prev.length >= MAX_ROWS ? prev : [...prev, { id: idCounter.current++, value: '' }]));
  }, []);

  const removeRow = useCallback((id) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  }, []);

  // Emails valides, dédupliqués (source unique pour l'envoi + l'UI conditionnelle)
  const validEmails = [];
  const seen = new Set();
  for (const r of rows) {
    const v = r.value.trim().toLowerCase();
    if (isValidEmail(v) && !seen.has(v)) { seen.add(v); validEmails.push(v); }
  }
  const hasFirstValid = validEmails.length >= 1;

  const handleSendInvites = useCallback(async (e) => {
    e.preventDefault();
    if (validEmails.length === 0) return;
    setInviteStatus('sending');
    setInviteMessage('');
    try {
      const sentCount = validEmails.length;
      await sendReferralInvite(validEmails);
      setInviteStatus('sent');
      setInviteMessage(`${sentCount} invitation${sentCount > 1 ? 's' : ''} sent`);
      setTotalInvites((prev) => prev + sentCount);
      setRows([{ id: idCounter.current++, value: '' }]);
      if (typeof onInvited === 'function') onInvited(validEmails);
      setTimeout(() => setInviteStatus(null), 4000);
    } catch (err) {
      setInviteStatus('error');
      setInviteMessage(err?.message || 'Failed to send, please try again.');
    }
  }, [validEmails, onInvited]);

  const sending = inviteStatus === 'sending';

  return (
    <div>
      {/* URL row + copy icon button */}
      <LinkRow>
        <LinkInput
          readOnly
          value={referralLink}
          onFocus={(e) => e.target.select()}
          aria-label="Your referral link"
        />
        <CopyIconButton
          type="button"
          onClick={handleCopy}
          className={copied ? 'is-copied' : ''}
          aria-label={copied ? 'Link copied' : 'Copy referral link'}
          title={copied ? 'Copied' : 'Copy link'}
        >
          {copied ? <CheckGlyph /> : <CopyGlyph />}
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </CopyIconButton>
      </LinkRow>

      {/* Email invite — bloc dédié (liseré magenta), multi-row, "+" pour ajouter,
          Invite dès un email valide */}
      <InviteBox>
        <Label>Send Invitations</Label>
        <form onSubmit={handleSendInvites} noValidate>
        {rows.map((r, i) => {
          const invalid = r.value.trim() !== '' && !isValidEmail(r.value);
          return (
            <EmailRow key={r.id}>
              <EmailInput
                type="email"
                inputMode="email"
                maxLength={MAX_EMAIL_LEN}
                placeholder="friend@example.com"
                value={r.value}
                $invalid={invalid}
                onChange={(e) => setRowValue(r.id, e.target.value)}
                disabled={sending}
                aria-label={`Friend's email ${i + 1}`}
                aria-invalid={invalid}
                autoComplete="off"
              />
              {rows.length > 1 && (
                <RemoveRowBtn
                  type="button"
                  onClick={() => removeRow(r.id)}
                  disabled={sending}
                  aria-label={`Remove email ${i + 1}`}
                >✕</RemoveRowBtn>
              )}
            </EmailRow>
          );
        })}

        {hasFirstValid && (
          <AddRowBtn type="button" onClick={addRow} disabled={sending || rows.length >= MAX_ROWS}>
            <span aria-hidden="true">+</span> Add another email
          </AddRowBtn>
        )}

        <InviteFooter>
          {inviteMessage ? (
            <InviteStatus $error={inviteStatus === 'error'}>{inviteMessage}</InviteStatus>
          ) : (
            <InviteStatus>
              {hasFirstValid
                ? `${validEmails.length} email${validEmails.length > 1 ? 's' : ''} ready`
                : 'Enter a friend’s email to invite them.'}
            </InviteStatus>
          )}
          {hasFirstValid ? (
            <CtaButton type="submit" disabled={sending} style={{ flexShrink: 0 }}>
              {sending ? 'Sending' : 'Invite'}
            </CtaButton>
          ) : totalInvites > 0 ? (
            <TotalInvites>Total <b>{totalInvites}</b> invitation{totalInvites > 1 ? 's' : ''}</TotalInvites>
          ) : null}
        </InviteFooter>
        </form>
      </InviteBox>
    </div>
  );
}
